import React, { useEffect, useState } from "react";
import {
  Container, Card, CardBody, Table, Badge, Button, Spinner, Row, Col,
  Input, Modal, ModalHeader, ModalBody, ModalFooter, Alert,
} from "reactstrap";
import { useNavigate } from "react-router-dom";
import {
  deleteUserRecord,
  disableMaintenanceMode,
  enableMaintenanceMode,
  getAllUsers,
  pauseUserAccount,
  subscribeToMaintenanceStatus,
  unpauseUserAccount,
} from "../../helpers/firestoreService";
import { useAuthUser } from "../../hooks/AuthProvider";
import { useAdmin } from "../../hooks/useAdmin";

const AdminUserList = () => {
  document.title = "Admin — Users";

  const user = useAuthUser();
  const { isAdmin, adminLoading } = useAdmin();
  const navigate = useNavigate();

  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("all");
  const [search, setSearch] = useState("");
  const [acting, setActing] = useState(null);
  const [maintenance, setMaintenance] = useState({ enabled: false, message: "" });
  const [maintenanceModal, setMaintenanceModal] = useState(false);
  const [maintenanceMessage, setMaintenanceMessage] = useState("Porcher is temporarily unavailable while we make improvements. Please check back soon.");
  const [maintenanceSaving, setMaintenanceSaving] = useState(false);

  // Delete modal
  const [deleteModal, setDeleteModal] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleteSaving, setDeleteSaving] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      setUsers(await getAllUsers());
    } catch (e) {
      console.error("AdminUserList load error:", e);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!adminLoading && !isAdmin) navigate("/overview");
  }, [isAdmin, adminLoading]);

  useEffect(() => {
    if (isAdmin) load();
  }, [isAdmin]);

  useEffect(() => {
    if (!isAdmin) return undefined;
    return subscribeToMaintenanceStatus(setMaintenance, (error) => {
      console.error("Maintenance status error:", error);
    });
  }, [isAdmin]);

  const handleMaintenance = async () => {
    setMaintenanceSaving(true);
    try {
      if (maintenance.enabled) {
        await disableMaintenanceMode();
        setMaintenanceModal(false);
      } else {
        await enableMaintenanceMode(maintenanceMessage, user.uid);
        setMaintenanceModal(false);
      }
    } catch (error) {
      console.error("Maintenance mode update error:", error);
    } finally {
      setMaintenanceSaving(false);
    }
  };

  const handleTogglePause = async (u) => {
    setActing(u.id);
    try {
      const paused = (u.accountStatus || "active") !== "paused";
      if (paused) {
        await pauseUserAccount(u.id, user.uid);
      } else {
        await unpauseUserAccount(u.id, user.uid);
      }
      setUsers((prev) => prev.map((x) => x.id === u.id ? { ...x, accountStatus: paused ? "paused" : "active" } : x));
    } catch (e) {
      console.error("Toggle pause error:", e);
    } finally {
      setActing(null);
    }
  };

  const openDelete = (u) => { setDeleteTarget(u); setDeleteModal(true); };

  const handleDelete = async () => {
    if (!deleteTarget) return;
    setDeleteSaving(true);
    try {
      await deleteUserRecord(deleteTarget.id);
      setUsers((prev) => prev.filter((x) => x.id !== deleteTarget.id));
      setDeleteModal(false);
    } catch (e) {
      console.error("Delete user error:", e);
    } finally {
      setDeleteSaving(false);
    }
  };

  if (adminLoading || loading) {
    return <div className="page-content text-center py-5"><Spinner color="primary" /></div>;
  }
  if (!isAdmin) return null;

  const filtered = users
    .filter((u) => filter === "all" || (u.accountStatus || "active") === filter)
    .filter((u) => !search || (u.email || "").toLowerCase().includes(search.toLowerCase()) || (u.displayName || "").toLowerCase().includes(search.toLowerCase()));

  const counts = {
    all: users.length,
    active: users.filter((u) => (u.accountStatus || "active") === "active").length,
    paused: users.filter((u) => u.accountStatus === "paused").length,
  };

  return (
    <div className="page-content">
      <Container fluid>

        {/* Header */}
        <div className="rounded mb-4 p-4 text-white position-relative overflow-hidden" style={{ minHeight: 130 }}>
          <div style={{ position: "absolute", inset: 0, background: "linear-gradient(135deg, #556ee6 0%, #6f42c1 100%)" }} />
          <div className="position-relative" style={{ zIndex: 1 }}>
            <Row className="align-items-center">
              <Col>
                <h3 className="mb-1 text-white">
                  <i className="bx bx-user-circle me-2" />Users
                </h3>
                <p className="mb-0 opacity-75">Every user on Porcher. Pause an account to deactivate its active rental listings.</p>
              </Col>
              <Col xs="auto">
                <Button color="light" size="sm" onClick={load}>
                  <i className="bx bx-refresh me-1" /> Refresh
                </Button>
              </Col>
            </Row>
            <Row className="mt-3 g-2">
              {["all", "active", "paused"].map((s) => (
                <Col xs="auto" key={s}>
                  <Badge
                    color={s === "paused" ? "danger" : s === "active" ? "success" : "light"}
                    className="fs-6 px-3 py-2"
                    style={{ cursor: "pointer", opacity: filter === s ? 1 : 0.65, textTransform: "capitalize" }}
                    onClick={() => setFilter(s)}
                  >
                    {s === "all" ? "All" : s.charAt(0).toUpperCase() + s.slice(1)}: {counts[s]}
                  </Badge>
                </Col>
              ))}
            </Row>
          </div>
        </div>

        <Card className="border-0 shadow-sm">
          <CardBody className="border-bottom">
            <Row className="align-items-center g-3">
              <Col>
                <div className="d-flex align-items-center gap-2 mb-1">
                  <h5 className="mb-0">Maintenance mode</h5>
                  <Badge color={maintenance.enabled ? "warning" : "success"}>
                    {maintenance.enabled ? "Active" : "Site online"}
                  </Badge>
                </div>
                <p className="text-muted mb-0 small">
                  {maintenance.enabled
                    ? "Everyone is seeing the maintenance page and normal client Firestore writes are blocked."
                    : "Temporarily take the site offline and block client Firestore writes."}
                </p>
              </Col>
              <Col xs="auto">
                <Button
                  color={maintenance.enabled ? "success" : "warning"}
                  onClick={() => setMaintenanceModal(true)}
                >
                  <i className={`bx ${maintenance.enabled ? "bx-play-circle" : "bx-power-off"} me-1`} />
                  {maintenance.enabled ? "End maintenance" : "Start maintenance"}
                </Button>
              </Col>
            </Row>
          </CardBody>
          <CardBody>
            <Row className="mb-3 align-items-center">
              <Col md={4}>
                <Input bsSize="sm" placeholder="Search by name or email…" value={search} onChange={(e) => setSearch(e.target.value)} />
              </Col>
              <Col xs="auto" className="ms-auto text-muted small">
                Showing {filtered.length} of {users.length}
              </Col>
            </Row>

            {filtered.length === 0 ? (
              <div className="text-center py-5 text-muted">
                <i className="bx bx-user-circle" style={{ fontSize: "2.5rem" }} />
                <p className="mt-2 mb-0">No users match this filter.</p>
              </div>
            ) : (
              <div className="table-responsive">
                <Table className="table-hover align-middle mb-0">
                  <thead className="table-light">
                    <tr>
                      <th>User</th>
                      <th>Email</th>
                      <th>Status</th>
                      <th>Registered</th>
                      <th className="text-end">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filtered.map((u) => {
                      const paused = u.accountStatus === "paused";
                      return (
                        <tr key={u.id}>
                          <td>
                            <div className="fw-semibold">{u.displayName || "—"}</div>
                            <small className="text-muted">{u.id}</small>
                          </td>
                          <td>{u.email || "—"}</td>
                          <td>
                            <Badge color={paused ? "danger" : "success"}>
                              {paused ? "Paused" : "Active"}
                            </Badge>
                          </td>
                          <td>
                            <small className="text-muted">
                              {u.createdAt ? u.createdAt.toLocaleDateString() : "—"}
                            </small>
                          </td>
                          <td className="text-end">
                            <div className="d-flex gap-2 justify-content-end flex-wrap">
                              <Button
                                color={paused ? "success" : "warning"}
                                outline
                                size="sm"
                                disabled={acting === u.id}
                                onClick={() => handleTogglePause(u)}
                                title={paused ? "Reactivate this account" : "Pause this account and deactivate its listings"}
                              >
                                {acting === u.id
                                  ? <Spinner size="sm" />
                                  : <><i className={`bx ${paused ? "bx-play-circle" : "bx-pause-circle"} me-1`} />{paused ? "Reactivate" : "Pause"}</>}
                              </Button>
                              <Button
                                color="danger"
                                size="sm"
                                disabled={acting === u.id}
                                onClick={() => openDelete(u)}
                                title="Delete account record"
                              >
                                <i className="bx bx-trash" />
                              </Button>
                            </div>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </Table>
              </div>
            )}
          </CardBody>
        </Card>
      </Container>

      {/* Delete Modal */}
      <Modal isOpen={deleteModal} toggle={() => !deleteSaving && setDeleteModal(false)}>
        <ModalHeader toggle={() => !deleteSaving && setDeleteModal(false)}>Delete Account Record</ModalHeader>
        <ModalBody>
          <Alert color="danger" className="py-2 small mb-3">
            <i className="bx bx-error me-1" />
            This removes the user's record from the database. Their Firebase Auth account will still exist — they can sign in again to recreate it.
          </Alert>
          <p className="mb-0">Delete record for <strong>{deleteTarget?.email || deleteTarget?.id}</strong>?</p>
        </ModalBody>
        <ModalFooter>
          <Button color="secondary" disabled={deleteSaving} onClick={() => setDeleteModal(false)}>Cancel</Button>
          <Button color="danger" disabled={deleteSaving} onClick={handleDelete}>
            {deleteSaving ? <Spinner size="sm" /> : <><i className="bx bx-trash me-1" />Delete Record</>}
          </Button>
        </ModalFooter>
      </Modal>

      <Modal isOpen={maintenanceModal} toggle={() => !maintenanceSaving && setMaintenanceModal(false)}>
        <ModalHeader toggle={() => !maintenanceSaving && setMaintenanceModal(false)}>
          {maintenance.enabled ? "End Maintenance Mode" : "Start Maintenance Mode"}
        </ModalHeader>
        <ModalBody>
          {maintenance.enabled ? (
            <p className="mb-0">Restore the site and allow client updates again?</p>
          ) : (
            <>
              <Alert color="warning" className="py-2 small">
                Visitors will be moved to the maintenance page and their Firestore writes will be rejected until you turn this off.
              </Alert>
              <label className="form-label" htmlFor="maintenance-message">Message shown to visitors</label>
              <Input
                id="maintenance-message"
                type="textarea"
                rows="3"
                value={maintenanceMessage}
                onChange={(event) => setMaintenanceMessage(event.target.value)}
              />
            </>
          )}
        </ModalBody>
        <ModalFooter>
          <Button color="secondary" disabled={maintenanceSaving} onClick={() => setMaintenanceModal(false)}>Cancel</Button>
          <Button
            color={maintenance.enabled ? "success" : "warning"}
            disabled={maintenanceSaving || (!maintenance.enabled && !maintenanceMessage.trim())}
            onClick={handleMaintenance}
          >
            {maintenanceSaving ? <Spinner size="sm" /> : maintenance.enabled ? "Restore site" : "Start maintenance"}
          </Button>
        </ModalFooter>
      </Modal>
    </div>
  );
};

export default AdminUserList;
